import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { autoReRequestApi, type SchedulesListResponse } from '@/services/api/autoReRequest'
import type { AutoReRequestSchedule, AutoReRequestHistoryItem } from '@/types/admin5'
import { extractErrorMessage } from '@/utils/errorHandler'
import { useToast } from '@/components/ui/ToastProvider'
import { getStatusColor, getStatusLabel } from '@/utils/statusHelpers'
import { RefreshCw, ChevronDown, ChevronRight, Play, Square } from 'lucide-react'

function formatRelative(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now()
  const absMin = Math.abs(diff) / 60000
  if (absMin < 60) return diff > 0 ? `in ${Math.round(absMin)} Min.` : `vor ${Math.round(absMin)} Min.`
  const absH = absMin / 60
  if (absH < 24) return diff > 0 ? `in ${Math.round(absH)} Std.` : `vor ${Math.round(absH)} Std.`
  const absD = absH / 24
  return diff > 0 ? `in ${Math.round(absD)} Tg.` : `vor ${Math.round(absD)} Tg.`
}

export function AutoReRequestPage() {
  const { showToast } = useToast()
  const [schedules, setSchedules] = useState<AutoReRequestSchedule[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'active' | 'all'>('active')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [expandedLesson, setExpandedLesson] = useState<string | null>(null)
  const [history, setHistory] = useState<AutoReRequestHistoryItem[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const initRef = useRef(false)

  const fetchSchedules = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true)
    try {
      const res: SchedulesListResponse = await autoReRequestApi.listSchedules({ include_past: filter === 'all' })
      setSchedules(res.schedules || [])
      setError(null)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Schedules konnten nicht geladen werden.' }))
    } finally {
      setLoading(false); setRefreshing(false)
    }
  }, [filter])

  useEffect(() => {
    if (initRef.current) { fetchSchedules(true) } else { initRef.current = true; fetchSchedules() }
  }, [fetchSchedules])

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchSchedules(true)
    }, 60000)
    return () => clearInterval(id)
  }, [fetchSchedules])

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const handleTrigger = async (lessonRequestId: string, subjectName?: string) => {
    if (!window.confirm(`Auto Re-Request jetzt für "${subjectName || 'Lektion'}" auslösen?`)) return
    setBusyId(lessonRequestId)
    try {
      await autoReRequestApi.triggerNow(lessonRequestId)
      showToast({ message: 'Re-Request ausgelöst.', type: 'success' })
      fetchSchedules(true)
    } catch (err) {
      showToast({ message: extractErrorMessage(err, { loadError: 'Re-Request konnte nicht ausgelöst werden.' }), type: 'error' })
    } finally { setBusyId(null) }
  }

  const handleCancel = async (scheduleId: string) => {
    if (!window.confirm('Schedule wirklich stoppen?')) return
    setBusyId(scheduleId)
    try {
      await autoReRequestApi.cancelSchedule(scheduleId)
      showToast({ message: 'Schedule gestoppt.', type: 'success' })
      fetchSchedules(true)
    } catch (err) {
      showToast({ message: extractErrorMessage(err, { loadError: 'Konnte Schedule nicht stoppen.' }), type: 'error' })
    } finally { setBusyId(null) }
  }

  const handleExpandHistory = async (lessonRequestId: string) => {
    if (expandedLesson === lessonRequestId) { setExpandedLesson(null); return }
    setExpandedLesson(lessonRequestId)
    setHistoryLoading(true)
    try {
      const res = await autoReRequestApi.getHistory(lessonRequestId)
      setHistory(res.history || [])
    } catch {
      setHistory([])
    } finally { setHistoryLoading(false) }
  }

  const filtered = schedules.filter(s => {
    if (filter === 'active' && s.status !== 'scheduled') return false
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase()
      const subject = s.lesson_details?.subject_name?.toLowerCase() || ''
      const slot = s.lesson_details?.time_slot_name?.toLowerCase() || ''
      return subject.includes(q) || slot.includes(q)
    }
    return true
  })

  const activeCount = schedules.filter(s => s.status === 'scheduled').length
  const nextSchedule = schedules
    .filter(s => s.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())[0]

  if (loading) return <div className="p-6 text-gray-500">Laden...</div>

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Auto Re-Request Verwaltung</h1>
          <p className="text-sm text-gray-500 mt-1">Alle automatischen Neuanfragen im Überblick · manuell steuern</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche Lektion…"
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 w-48" />
          <button onClick={() => fetchSchedules(true)} disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Aktive Schedules</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{activeCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Nächste Anfrage</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">
            {nextSchedule ? formatRelative(nextSchedule.scheduled_at) : '—'}
          </div>
          {nextSchedule?.lesson_details?.subject_name && (
            <div className="text-xs text-gray-500 mt-1">{nextSchedule.lesson_details.subject_name}</div>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Gesamt Schedules</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{schedules.length}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Gefiltert</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{filtered.length}</div>
        </div>
      </div>

      <div className="flex gap-1 mb-4">
        <button onClick={() => setFilter('active')}
          className={`px-3 py-1.5 text-xs rounded-md ${filter === 'active' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
          Aktive ({activeCount})
        </button>
        <button onClick={() => setFilter('all')}
          className={`px-3 py-1.5 text-xs rounded-md ${filter === 'all' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
          Alle ({schedules.length})
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
          <p>Keine Auto Re-Request Schedules vorhanden</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium w-8"></th>
                  <th className="px-4 py-2.5 text-left font-medium">Lektion</th>
                  <th className="px-4 py-2.5 text-left font-medium">Versuche</th>
                  <th className="px-4 py-2.5 text-left font-medium">Nächste Anfrage</th>
                  <th className="px-4 py-2.5 text-left font-medium">Intervall</th>
                  <th className="px-4 py-2.5 text-left font-medium">Status</th>
                  <th className="px-4 py-2.5 text-right font-medium">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(s => (
                  <Fragment key={s.schedule_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button onClick={() => handleExpandHistory(s.lesson_request_id)} className="text-gray-400 hover:text-gray-600">
                          {expandedLesson === s.lesson_request_id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{s.lesson_details?.subject_name || '—'}</div>
                        {s.lesson_details?.time_slot_name && <div className="text-xs text-gray-500">{s.lesson_details.time_slot_name}</div>}
                        {s.lesson_details?.date && <div className="text-xs text-gray-500">{s.lesson_details.date}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{s.re_request_number} / {s.max_re_requests}</span>
                      </td>
                      <td className="px-4 py-3">
                        {s.status === 'scheduled' ? (
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            <span className="font-medium">{formatRelative(s.scheduled_at)}</span>
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{s.delay_hours} Std.</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(s.status)}`}>
                          {getStatusLabel(s.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {s.status === 'scheduled' && (
                            <>
                              <button onClick={() => handleTrigger(s.lesson_request_id, s.lesson_details?.subject_name ?? undefined)}
                                disabled={busyId === s.lesson_request_id}
                                className="text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                                <Play className="w-3 h-3" /> Jetzt
                              </button>
                              <button onClick={() => handleCancel(s.schedule_id)}
                                disabled={busyId === s.schedule_id}
                                className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                                <Square className="w-3 h-3" /> Stoppen
                              </button>
                            </>
                          )}
                          {s.status !== 'scheduled' && (
                            <button onClick={() => handleTrigger(s.lesson_request_id, s.lesson_details?.subject_name ?? undefined)}
                              disabled={busyId === s.lesson_request_id}
                              className="text-xs text-green-600 hover:bg-green-50 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                              <Play className="w-3 h-3" /> Auslösen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedLesson === s.lesson_request_id && (
                      <tr>
                        <td colSpan={7} className="px-8 py-4 bg-gray-50">
                          {historyLoading ? (
                            <p className="text-sm text-gray-500">Verlauf wird geladen…</p>
                          ) : history.length === 0 ? (
                            <p className="text-sm text-gray-500">Kein Verlauf vorhanden.</p>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Verlauf</p>
                              {history.map((h, i) => (
                                <div key={i} className="flex items-center gap-3 text-sm">
                                  <span className={`w-2 h-2 rounded-full ${h.resulted_in_acceptance ? 'bg-green-500' : h.acceptances > 0 ? 'bg-green-400' : h.declines > 0 ? 'bg-red-400' : 'bg-gray-300'}`} />
                                  <span className="text-gray-900 font-medium">Anfrage {h.re_request_number}</span>
                                  <span className="text-gray-500">{h.teachers_contacted} kontaktiert</span>
                                  <span className="text-green-600">{h.acceptances} angenommen</span>
                                  <span className="text-red-500">{h.declines} abgelehnt</span>
                                  <span className="text-gray-400">{formatRelative(h.executed_at)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
